import {
  Button,
  ButtonGroup,
  Classes,
  FormGroup,
  InputGroup,
} from '@blueprintjs/core';
import { ConfirmationResult, RecaptchaVerifier } from '@firebase/auth-types';
import * as React from 'react';
import { DialogPanelContainer } from '.';
import firebase from '../../firebase';
import { confirmSignInWithMobile, signInWithMobile } from '../../store/actions';

declare global {
  const grecaptcha: ReCaptcha;

  interface ReCaptcha {
    reset(optWidgetId?: string): void;
  }

  interface Window {
    recaptchaVerifier: RecaptchaVerifier;
  }
}

export interface ISignInMobileOwnProps {
  onCloseDialog: () => void;
  onSetAlert: (message: string) => void;
  onToggleDialogLock: () => void;
}

export type SignInMobileProps = ISignInMobileOwnProps;

interface ISignInMobileState {
  confirmation: string;
  confirmationResult?: ConfirmationResult;
  confirmationTouched: boolean;
  confirmationValid: boolean;
  confirming: boolean;
  formDisabled: boolean;
  loadingSubmitConfirmation: boolean;
  loadingSubmitMobile: boolean;
  mobile: string;
  mobileTouched: boolean;
  mobileValid: boolean;
}

export type SignInMobileState = ISignInMobileState;

class LoginMobile extends React.Component<
  SignInMobileProps,
  SignInMobileState
> {
  public initialState: SignInMobileState = {
    confirmation: '',
    confirmationTouched: false,
    confirmationValid: false,
    confirming: false,
    formDisabled: false,
    loadingSubmitConfirmation: false,
    loadingSubmitMobile: false,
    mobile: '',
    mobileTouched: false,
    mobileValid: false,
  };

  constructor(props: SignInMobileProps) {
    super(props);

    this.state = this.initialState;
  }

  public componentDidMount() {
    window.recaptchaVerifier = new firebase.auth.RecaptchaVerifier(
      'grecaptcha-button',
      {
        size: 'invisible',
      },
    );
  }

  public componentDidUpdate(prevProps: never, prevState: SignInMobileState) {
    const { onToggleDialogLock } = this.props;
    const { loadingSubmitMobile: prevLoadingSubmitMobile } = prevState;
    const { loadingSubmitMobile } = this.state;

    if (prevLoadingSubmitMobile !== loadingSubmitMobile) {
      onToggleDialogLock();
    }
  }

  public render() {
    const { onCloseDialog } = this.props;
    const {
      confirmation,
      confirmationTouched,
      confirmationValid,
      confirming,
      formDisabled,
      loadingSubmitConfirmation,
      loadingSubmitMobile,
      mobile,
      mobileTouched,
      mobileValid,
    } = this.state;
    const confirmationIntent = confirmationTouched
      ? confirmationValid
        ? 'success'
        : 'danger'
      : undefined;
    const mobileIntent = mobileTouched
      ? mobileValid
        ? 'success'
        : 'danger'
      : undefined;
    const submitButtonAction = confirming
      ? this.submitConfirmation
      : this.submitMobile;
    const submitButtonDisabled = confirming
      ? !confirmationValid || loadingSubmitConfirmation
      : !mobileValid || loadingSubmitMobile;
    const submitButtonLoading =
      loadingSubmitConfirmation || loadingSubmitMobile;

    return (
      <DialogPanelContainer className={Classes.DIALOG_BODY}>
        {confirming ? (
          <FormGroup
            disabled={formDisabled}
            intent={confirmationIntent}
            label="verification code"
            labelFor="confirmation-input">
            <InputGroup
              disabled={loadingSubmitConfirmation}
              id="confirmation-input"
              intent={confirmationIntent}
              name="confirmation"
              onBlur={this.touchConfirmation}
              onChange={this.handleInputChange}
              type="confirmation"
              value={confirmation}
            />
          </FormGroup>
        ) : (
          <FormGroup
            disabled={formDisabled}
            intent={mobileIntent}
            label="mobile number"
            labelFor="mobile-input">
            <InputGroup
              disabled={loadingSubmitMobile}
              id="mobile-input"
              intent={mobileIntent}
              name="mobile"
              onBlur={this.touchMobile}
              onChange={this.handleInputChange}
              type="mobile"
              value={mobile}
            />
          </FormGroup>
        )}
        <ButtonGroup>
          {confirming && (
            <Button intent="danger" onClick={onCloseDialog}>
              cancel
            </Button>
          )}
          <Button
            disabled={submitButtonDisabled}
            id="grecaptcha-button"
            intent="primary"
            loading={submitButtonLoading}
            onClick={submitButtonAction}
            text={confirming ? 'sign in' : 'get confirmation code'}
          />
        </ButtonGroup>
      </DialogPanelContainer>
    );
  }

  private handleInputChange = (event: React.FormEvent<HTMLInputElement>) => {
    const { name, value } = event.currentTarget;

    if (name === 'confirmation') {
      const cleanValue = value.replace(/\D/g, '').substring(0, 6);
      const confirmation = this.handleConfirmation(cleanValue);

      this.setState({ confirmation });
    }

    if (name === 'mobile') {
      const cleanValue = value.replace(/\D/g, '').substring(0, 10);
      const mobile = this.handleMobile(cleanValue);

      this.setState({ mobile });
    }
  };

  private handleConfirmation = (confirmation: string) => {
    const confirmationRegex = /\d{6}/g;
    const confirmationValid =
      confirmation.length === 6 && confirmationRegex.test(confirmation);

    if (confirmationValid) {
      this.setState({ confirmationTouched: true, confirmationValid });
    } else {
      this.setState({ confirmationValid });
    }

    return confirmation;
  };

  private handleMobile = (mobile: string) => {
    const mobileRegex = /\d{10}/g;
    const mobileValid = mobile.length === 10 && mobileRegex.test(mobile);

    if (mobileValid) {
      this.setState({ mobileTouched: true, mobileValid });
    } else {
      this.setState({ mobileValid });
    }

    return mobile;
  };

  private handleMobileSubmitError = async (error: string) => {
    const { onSetAlert } = this.props;

    onSetAlert(error);

    const widgetId = await window.recaptchaVerifier.render();

    return grecaptcha.reset(widgetId.toString());
  };

  private submitConfirmation = async () => {
    const { onCloseDialog } = this.props;
    const { confirmation, confirmationResult } = this.state;

    if (confirmationResult) {
      this.setState({ loadingSubmitConfirmation: true });

      const result = await confirmSignInWithMobile(
        confirmation,
        confirmationResult,
      );

      this.setState({ loadingSubmitConfirmation: false });

      if (typeof result === 'string') {
        console.error(result);
      } else {
        onCloseDialog();
      }
    }
  };

  private submitMobile = async () => {
    const { onToggleDialogLock } = this.props;
    const { mobile } = this.state;
    const mobileVerifier = window.recaptchaVerifier;

    onToggleDialogLock();
    this.setState({ loadingSubmitMobile: true });

    const result = await signInWithMobile(mobile, mobileVerifier);

    this.setState({ loadingSubmitMobile: false });

    if (typeof result === 'string') {
      this.handleMobileSubmitError(result);
    } else {
      this.setState({ confirmationResult: result, confirming: true });
    }
  };

  private touchConfirmation = () =>
    this.setState({ confirmationTouched: true });

  private touchMobile = () => this.setState({ mobileTouched: true });
}

export default LoginMobile;



// WEBPACK FOOTER //
// ./src/components/SignIn/Mobile.tsx